import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import Button from "../../Button";
import Editor from "../../Editor";
import { ReactComponent as ReviewSVG } from "../../../assets/svg/review.svg";
import { ReactComponent as CheckSVG } from "../../../assets/svg/check.svg";
import { useAttempt } from "../../../hooks/useAttempt";
import { useLessonDetails } from "../../../hooks/useLessonDetails";
import LessonCartaReview from "./LessonCartaReview";
import ModalForm from "../../ModalForm";
import LessonAPI from "../../../apis/LessonAPI";
import ModalNotification from "../../ModalNotification";

const labelState = {
  IN_REVIEW: "Falta revisar",
  SUCCESS: "Revisado",
  FAILURE: "Revisado con errores",
  COMPLETING: "En desarrollo",
};

const LessonCartaView = ({
  courseId,
  episodeId,
  chapterId,
  lessonId,
  userId,
}) => {
  const lesson = useLessonDetails(lessonId);

  const description = lesson?.description;
  const attempt = useAttempt(lessonId, userId);
  const state = attempt?.state || "NOT_SENDED";

  const [text, setText] = useState("");

  const [modal, setModal] = useState({ show: false });
  const [isSending, setIsSending] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!attempt) return;

    setText(attempt.text || "");
  }, [attempt]);

  const success = () => {
    setIsSending(true);
    LessonAPI.LESSON_CARTA.success(lessonId, userId)
      .then(() =>
        LessonAPI.LESSON_CARTA.notify(userId, {
          courseId,
          episodeId,
          chapterId,
          lessonId,
        })
      )
      .then(() => {
        setIsSending(false);
        setShowConfirmModal(false);
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <>
      {modal.show && (
        <ModalForm
          title="Evaluación"
          isOpen={modal.show}
          onClose={() => setModal({ show: false })}
        >
          <LessonCartaReview
            courseId={courseId}
            episodeId={episodeId}
            chapterId={chapterId}
            lessonId={lessonId}
            userId={userId}
            onClose={() => setModal({ show: false })}
            onFinish={() => setModal({ show: false })}
          />
        </ModalForm>
      )}

      <Container>
        {attempt?.attempt > 0 && <Tag>Intento #{attempt?.attempt}</Tag>}
        <TagState state={state}>
          {labelState[state] || "En desarrollo"}
        </TagState>
        {description && (
          <Description
            as={Editor}
            theme={null}
            isMinimal={true}
            readOnly={true}
            value={description}
          />
        )}
        <Content>
          {state === "IN_REVIEW" && (
            <MessageWrapper>
              <ReviewIconSVG as={ReviewSVG} />
              <TextSuccess>
                {`La carta está siendo evaluada.\nRecibirás una notificación cuando la evaluación haya terminado.`}
              </TextSuccess>
            </MessageWrapper>
          )}
          {state === "SUCCESS" && (
            <MessageWrapper>
              <CheckIconSVG as={CheckSVG} />
              <TextSuccess>La carta está escrita correctamente!</TextSuccess>
            </MessageWrapper>
          )}
          {state === "FAILURE" && attempt?.observations && (
            <ObservationsWrapper>
              <Title>Observaciones</Title>
              <Observations
                as={Editor}
                theme={null}
                isMinimal={true}
                readOnly={true}
                value={attempt.observations}
              />
            </ObservationsWrapper>
          )}
          <InputWrapper state={state}>
            <InputTextAreaStyled
              as={Editor}
              theme={null}
              value={text}
              onChange={setText}
              placeholder="Aún no escribe nada el usuario..."
              readOnly={true}
              minHeight={200}
            />
          </InputWrapper>
        </Content>
        {state === "IN_REVIEW" && (
          <Footer>
            <ButtonCheck
              text="Todo está bien"
              onClick={() => {
                setShowConfirmModal(true);
              }}
            />
            <ButtonReview
              text="Corregir"
              onClick={() => setModal({ show: true })}
            />
          </Footer>
        )}

        {showConfirmModal && (
          <ModalNotification
            isOpen={true}
            title="Confirmación"
            description={`¿Estás seguro de que todo está bien?`}
            type="question"
            buttons={
              <>
                <Button
                  secondary={true}
                  text="Cancelar"
                  onClick={() => setShowConfirmModal(false)}
                />
                <Button
                  primary={true}
                  text="Enviar"
                  loading={isSending}
                  onClick={() => success()}
                />
              </>
            }
          />
        )}
      </Container>
    </>
  );
};


const Description = styled.div`
  font-size: 0.9em;
  color: white !important;
  background-color: transparent !important;

  & * {
    color: white !important;
    background-color: transparent !important;
  }

  margin-bottom: 25px;
  /* white-space: pre-wrap; */
`;

const Content = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 15px;
  }
`;

const Container = styled.div``;

const MessageWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  padding: 15px;
  background-color: white;
  border-radius: 15px;
`;

const CheckIconSVG = styled.div`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
`;

const ReviewIconSVG = styled.div`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
  fill: #607d8b;
`;

const TextSuccess = styled.div`
  margin-left: 15px;
  white-space: pre-wrap;
`;

const ObservationsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding: 15px;
  background-color: white;
  border-radius: 15px;
`;

const Title = styled.div`
  font-size: 1em;
  font-weight: bold;
  color: red;

  margin-bottom: 10px;
`;

const Observations = styled.div`
  font-size: 0.9em;
  color: black !important;
  background-color: transparent !important;

  & * {
    color: black !important;
    background-color: transparent !important;
  }
`;

const InputWrapper = styled.div`
  ${(props) => {
    if (props.state === "FAILURE")
      return css`
        & b,
        & strong {
          color: black;
        }
      `;
    else
      return css`
        & b,
        & strong {
          color: black;
          font-weight: normal;
        }
      `;
  }}
`;

const InputTextAreaStyled = styled.div`
  /* min-height: 250px; */

  border-radius: 15px;
  background-color: white;
`;

const Footer = styled.div`
  display: flex;
  margin-top: 25px;

  & > *:not(:last-child) {
    margin-right: 15px;
  }
`;

const ButtonReview = styled(Button)`
  .button {
    background-color: #f44336;
    color: white;
  }

  flex: 1;
`;

const ButtonCheck = styled(Button)`
  .button {
    background-color: #00c853;
    color: white;
  }

  flex: 1;
`;

const Tag = styled.div`
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 10px 10px;
  font-size: 0.9em;
  font-weight: 600;
  margin: 0px 0px 5px 0px;
`;

const TagState = styled.div`
  border-radius: 10px;
  color: white;
  padding: 10px 10px;
  margin-bottom: 10px;
  font-size: 0.9em;
  font-weight: 600;
  margin: 0px 0px 25px 0px;

  ${(props) => {
    if (props.state === "IN_REVIEW")
      return css`
        background-color: #f44336;
        color: white;
      `;
    else if (props.state === "SUCCESS" || props.state === "FAILURE")
      return css`
        background-color: #4caf50;
        color: white;
      `;
    else
      return css`
        background-color: rgba(0, 0, 0, 0.5);
      `;
  }}
`;

export default LessonCartaView;
